import React, { useEffect, useState } from 'react';
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.css";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import CardDetails from './components/CardDetails';
import Header from './components/Header';
import Footer from './components/Footer';
import Homemycards from './components/Homemycards';
import Homemyholder from './components/Homemyholder';
import Homewebholder from './components/Homewebholder';
import Login from './components/Login';
import Signup from './components/Signup';
import Otp from './components/Otp';
import AddNewCard from './components/AddNewCard';
import AddNewCard2 from './components/AddNewCard2';
import Notification from './components/Notification'
import SharedCard from './components/SharedCard';
import ResetPassword from './components/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import { ThemeProvider } from './components/ThemeContext';
import { checkAuthentication } from './utils/authService';
import Loader from './components/Loader';
import LandingPage from './components/LandingPage'; // Import the LandingPage component

// Component to redirect authenticated users away from public routes (landing, login, signup)
const PublicRoute = ({ children }) => {
  const [authStatus, setAuthStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const status = await checkAuthentication();
        setAuthStatus(status);
      } catch (error) {
        console.error('Auth check error:', error);
        setAuthStatus({ isAuthenticated: false, offlineMode: false });
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  const isAuthed = authStatus?.isAuthenticated || authStatus?.offlineMode;

  if (isAuthed) {
    // If authenticated, redirect to home
    const redirectTo = location.state?.from || '/home-web-holder';
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

const App = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [authStatus, setAuthStatus] = useState({
    isAuthenticated: false,
    offlineMode: false,
  });

  useEffect(() => {
    // Check authentication on app startup
    const initializeApp = async () => {
      try {
        const status = await checkAuthentication();
        setAuthStatus(status);
      } catch (error) {
        console.error('App initialization error:', error);
        setAuthStatus({ isAuthenticated: false, offlineMode: false });
      } finally {
        setIsInitializing(false);
      }
    };

    initializeApp();

    // Listen for online/offline events to re-check authentication when coming back online
    const handleOnline = async () => {
      console.log('Device came back online, re-checking authentication...');
      try {
        const status = await checkAuthentication();
        setAuthStatus(status);
        
        // If authentication failed after coming back online (refresh failed), log out
        if (!status.isAuthenticated && !status.offlineMode) {
          // Clear tokens and cached data since refresh failed
          const { clearTokens } = await import('./utils/authService');
          clearTokens();
          
          // Only redirect if we're not already on landing, login, or signup pages
          if (!window.location.pathname.includes('/landing') && 
              !window.location.pathname.includes('/login') && 
              !window.location.pathname.includes('/signup')) {
            window.location.href = '/landing';
          }
        }
      } catch (error) {
        console.error('Error re-checking authentication after coming online:', error);
        // On error, clear tokens and redirect to landing
        const { clearTokens } = await import('./utils/authService');
        clearTokens();
        if (!window.location.pathname.includes('/landing') && 
            !window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup')) {
          window.location.href = '/landing';
        }
      }
    };

    const handleOffline = async () => {
      console.log('Device went offline');
      // When going offline, check if user has logged in before and set offline mode
      const { hasLoggedInBefore, setOfflineMode } = await import('./utils/authService');
      const hasLoggedIn = hasLoggedInBefore();
      if (hasLoggedIn) {
        setOfflineMode(true);
        setAuthStatus({ isAuthenticated: true, offlineMode: true });
      } else {
        // If never logged in, force landing page
        setAuthStatus({ isAuthenticated: false, offlineMode: false });
        if (!window.location.pathname.includes('/landing') && 
            !window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup')) {
          window.location.href = '/landing';
        }
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isInitializing) {
    return <Loader />;
  }

  const isAuthed = authStatus.isAuthenticated || authStatus.offlineMode;

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* Landing page - shown to unauthenticated users */}
          <Route 
            path="/" 
            element={
              isAuthed ? (
                <Navigate to="/home-web-holder" />
              ) : (
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              )
            } 
          />
          
          {/* Explicit landing page route */}
          <Route 
            path="/landing" 
            element={
              isAuthed ? (
                <Navigate to="/home-web-holder" />
              ) : (
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              )
            } 
          />
          
          {/* Login route */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          <Route 
            path="/signup" 
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            } 
          />
          
          <Route path="/otp-verification" element={<Otp />} />
          
          {/* Protected routes - require authentication */}
          <Route 
            path="/home-my-cards" 
            element={
              <ProtectedRoute>
                <Homemycards />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/home-my-holder" 
            element={
              <ProtectedRoute>
                <Homemyholder />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/home-web-holder" 
            element={
              <ProtectedRoute>
                <Homewebholder />
              </ProtectedRoute>
            } 
          />
          <Route path="/sidebar" element={<Sidebar />} />
          <Route path="/header" element={<Header />} />
          <Route path="/footer" element={<Footer />} />
          <Route 
            path="/card-details/:id" 
            element={
              <ProtectedRoute>
                <CardDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-new-card" 
            element={
              <ProtectedRoute>
                <AddNewCard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-new-card2" 
            element={
              <ProtectedRoute>
                <AddNewCard2 />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notification" 
            element={
              <ProtectedRoute>
                <Notification />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/edit-card/:id" 
            element={
              <ProtectedRoute>
                <AddNewCard editMode={true} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/edit-card/:id/step2" 
            element={
              <ProtectedRoute>
                <AddNewCard2 editMode={true} />
              </ProtectedRoute>
            } 
          />
          <Route path="/shared-card/:id" element={<SharedCard />} />
          <Route 
            path="/reset-password" 
            element={
              <ProtectedRoute>
                <ResetPassword />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;